import { HighlightedFeedbackText } from '@modules/feedback/components/FeedbackHighlighter/HighlightedFeedbackText';
import { FEEDBACK_MESSAGE_ACTOR_ID_FALLBACK } from '@modules/feedback/constants';
import { Highlight } from '@modules/feedback/types';
import { Avatar, Divider, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { ForumContent } from 'v2/core/entities/feedback/content';

interface ForumMessageTypePreviousMessagesProps {
  showOriginalLanguage: boolean;
  content?: ForumContent;
  highlights: {
    [key: string]: Highlight[];
  };
  shouldDisableIndent: boolean;
  isCollapsed: boolean;
}

const ForumMessageTypePreviousMessages = ({
  showOriginalLanguage,
  content,
  highlights,
  shouldDisableIndent,
  isCollapsed,
}: ForumMessageTypePreviousMessagesProps) => {
  const [containsHighlights, setContainsHighlights] = useState(false);

  useEffect(() => {
    // Figure out if any of the PreviousMessages contain highlights. For
    // PreviousMessages, the index can start from 1. Also, the PreviousMessages
    // is an array, so we need to loop through it.
    content?.parentMsgs?.forEach((_, index) => {
      if (Object.keys(highlights).indexOf((index + 1).toString()) > -1) {
        setContainsHighlights(true);
      }
    });
  }, [content?.parentMsgs, highlights]);

  const visibleParentMsgs = isCollapsed
    ? content?.parentMsgs?.slice(-1)
    : content?.parentMsgs;

  if (!visibleParentMsgs) {
    return null;
  }

  return (
    <Stack spacing={4} pt={4}>
      {visibleParentMsgs.map((msg, index) => {
        return (
          <Box key={index}>
            <Stack
              spacing={2}
              sx={{
                marginLeft: shouldDisableIndent ? 0 : index * 4,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: 'divider',
                    fontSize: 14,
                    fontWeight: 'bold',
                  }}
                >
                  {msg.actorID.slice(0, 1)}
                </Avatar>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  sx={{
                    wordBreak: 'break-word',
                  }}
                >
                  {msg.actorID || FEEDBACK_MESSAGE_ACTOR_ID_FALLBACK}
                </Typography>
              </Stack>
              <Box position="relative">
                {!shouldDisableIndent && (
                  <Divider
                    sx={{
                      // Setting height to a max for covering all edge cases
                      height: `calc(100vh * ${
                        content?.parentMsgs?.length || 1
                      })`,
                      top: 0,
                      left: 8,
                      width: 0,
                      borderRightWidth: 2,
                    }}
                    absolute
                    component={Box}
                    orientation="vertical"
                  />
                )}
                <Typography
                  variant="body1"
                  sx={{
                    wordBreak: 'break-word',
                    borderLeft: shouldDisableIndent ? '2px solid' : null,
                    borderColor: shouldDisableIndent ? 'divider' : null,
                    paddingLeft: 4,
                    marginLeft: 1,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {containsHighlights ? (
                    <HighlightedFeedbackText
                      text={msg.text}
                      highlights={highlights[index + 1]}
                      showOriginalLanguage={showOriginalLanguage}
                    />
                  ) : (
                    msg.text
                  )}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

export default ForumMessageTypePreviousMessages;
